import * as Api from "@api";
import { CardImagePosition, Color, Typography } from '@/components';
import { HorizontalAlignment, Margin, MediaSpecific, Width } from "../Layout";
import { ImageCardImageIndentBasic, ImageCardProps } from "./ImageCard";
import VadImage, {VadImageProps} from "vadimages-nextjs-image-optimizer";

export type ImageCardStdSpecificProps =
    {
        betweenNode?: React.ReactNode;
        className?: string;
        textColor?: Color | string;
        bodyMaxWidth?: Width;
        bodyTypography?: Typography;
        headingMarginBottom?: Margin;
        headingMaxWidth?: Width;
        headingTypography?: Typography;
        imageWidth: number;
        imageHeight: number;
        imageExtention?: string;
        imageName?: string;
        imageNode?: React.ReactElement<React.ComponentProps<"figure">>;
        indentPaddingImage?: number;
        textLeft?: boolean;
        mobileTextLeft?: boolean;
        mainAxisOffset?: number;
        crossAxisOffset?: number;
        width?: Width;
        mobileImageProps?: Partial<React.ComponentProps<typeof VadImage>>;
        indentRight?: number;
        indentLeft?: number;
        indentAfter?: number;
        indentBefore?: number;
        indentImage?: MediaSpecific<ImageCardImageIndentBasic>;
        imagePosition: CardImagePosition;
        imageAlignment?: HorizontalAlignment;
        mobileImageAlignment?: HorizontalAlignment;
        mobileImagePosition?: CardImagePosition;
        overlapped?: boolean;
        link?: string;
        bgColor?: Color | string;
        animation?: { src: string, textOverAnimation?: boolean };
        badgeText?: string;
        badgeBackground?: string;
    };

export type ImageCardStdProps =
    ImageCardStdSpecificProps
    &
    {
        imageDirectory: string;
        keyBase: string;
        pageContent: Record<string, Api.ContentItem>;
    };


export const getImageCardStdProps = (props: ImageCardStdProps): ImageCardProps =>
{
    const {
        bodyMaxWidth,
        bodyTypography,
        crossAxisOffset,
        headingMarginBottom,
        headingMaxWidth,
        headingTypography,
        imageDirectory,
        imageHeight,
        imageName,
        imageExtention,
        imagePosition,
        mobileImagePosition,
        imageWidth,
        keyBase,
        mainAxisOffset,
        pageContent,
        width,
        mobileImageProps,
        betweenNode,
        textColor,
        textLeft = true,
        mobileTextLeft = false,
        bgColor = Color.MutedTan,
        link,
        animation,
        badgeText,
        badgeBackground,
    } = props;

    const keys = [`${keyBase}.headline`, `between-node`, `${keyBase}.body`] as const;

    const commonProps: Pick<ImageCardProps, "contentProps" | "imageProps" | "layout" | "imageIndent" | "useWrapper" | "mobileImagePosition" | "patent"> = {
        patent: pageContent[`${keyBase}.patent`]?.data,
        contentProps: {
            keys,
            pageContent,
            items: {
                [keys[0]]: {
                    props: {
                        centered: !textLeft,
                        mobileCentered: !mobileTextLeft,
                        margins: { bottom: headingMarginBottom ?? Margin.D20, x: !textLeft ? Margin.Auto : undefined },
                        layout: { ipad: { maxWidth: headingMaxWidth }, mobile: { maxWidth: undefined } },
                        typography: headingTypography ?? Typography.MH2DH3,
                    }
                },
                [keys[1]]: {
                    node: betweenNode
                },
                [keys[2]]: {
                    props: {
                        centered: !textLeft,
                        mobileCentered: !mobileTextLeft,
                        margins: { x: !textLeft ? Margin.Auto : undefined },
                        layout: { ipad: { maxWidth: bodyMaxWidth }, mobile: { maxWidth: undefined } },
                        typography: bodyTypography ?? Typography.P2,
                    }
                }
            }
        },
        imageProps: {
            src: `${imageDirectory}${imageName ?? keyBase}${imageExtention ?? ".png"}`,
            alt: pageContent[keys[0]]?.data,
            width: imageWidth,
            height: imageHeight
        },
        imageIndent: props.indentImage,
        mobileImagePosition,
        layout: {
            all: {
                width: Width.W100P,
                ...(!!props.indentPaddingImage ? {indentPaddingImage: props.indentPaddingImage} : {}) 
            },
            mobile: {},
            ipad: {
                mainAxisOffset,
                crossAxisOffset,
                width
            }
        },
        useWrapper: true
    };
    if (!!mobileImageProps) {
        commonProps.imageProps = {
            ...commonProps.imageProps as VadImageProps,
            ...mobileImageProps,
            mobileSrc: mobileImageProps.mobileSrc ? mobileImageProps.mobileSrc : `${imageDirectory}mobile/${imageName ?? keyBase}${imageExtention ?? ".png"}`,
         };
    }

    const propsApplied: ImageCardProps = {
        ...commonProps,
        imagePosition,
        link,
        bgColor,
        badgeText,
        badgeBackground,
        textColor: textColor ?? Color.WarmGray,
        animation,
        layout: {
            ...commonProps.layout,
            all: {
                ...commonProps.layout?.all,
                indentAfter: props.indentAfter,
                indentBefore: props.indentBefore,
                indentRight: props.indentRight,
                indentLeft: props.indentLeft,
            },
            ipad: {
                indentAfter: props.indentAfter,
                indentBefore: props.indentBefore,
                indentRight: props.indentRight,
                indentLeft: props.indentLeft,
                ...commonProps.layout?.ipad,
            }
        },
        sideProps: {
            imageAlignment: props.imageAlignment,
            mobileImageAlignment: props.mobileImageAlignment,
            ...(props.indentPaddingImage !== undefined ? { mobileImagePosition: props.mobileImagePosition } : { mobileImagePosition: CardImagePosition.Bottom }),
            overlapped: props.overlapped,
        }
    }

    return propsApplied;
}
